import React, { useState, useEffect, ComponentProps, useCallback } from "react"
import { theme } from "@/common/Mixin"
import styled, { useTheme } from "styled-components"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import Button from "./_UI/ButtonNew"

const Fade = require("react-reveal/Fade")

export interface StatisticItem {
  numberPrefix?: string
  number: number
  numberSuffix?: string
  description: string
  trend: "bigger" | "smaller"
}

interface StatisticProps {
  title: string
  id?: string
  description?: string
  items: StatisticItem[]
  buttonText?: string
  buttonHref?: ComponentProps<"a">["href"]
}

interface ItemWithAnimation {
  numberPrefix?: string
  curNumber: number
  numberSuffix?: string
  description: string
  step: number
}

export default function Statistic({
  title,
  id,
  description,
  items,
  buttonText,
  buttonHref
}: StatisticProps) {
  gsap.registerPlugin(ScrollTrigger)
  const theme = useTheme()
  const [count, setCount] = useState<ItemWithAnimation[]>([])

  const counterAnimation = useCallback(() => {
    const totalStep = 200
    const initItems = items.map(item => {
      const start = item.trend === "bigger" ? 0 : item.number * 9
      return {
        ...item,
        curNumber: start,
        step: (item.number - start) / totalStep
      }
    })
    for (let i = 1; i <= totalStep; i++) {
      setTimeout(() => {
        setCount(
          initItems.map(item => {
            return {
              ...item,
              curNumber: Math.floor(item.curNumber + item.step * i)
            }
          })
        )
      }, 7 * i)
    }
  }, [items])

  useEffect(() => {
    ScrollTrigger.create({
      trigger: ".counterBox2",
      start: "top 80%",
      end: "bottom 20%",
      // markers: true,
      onEnter: () => {
        counterAnimation()
      }
    })
  }, [counterAnimation])

  return (
    <Wrapper id={id} className="counterBox2 kg-wrapper">
      <TitleWrapper className="title">{title}</TitleWrapper>
      <DescWrapper
        style={{ color: theme.colors.textFamily.disabled }}
        className="body"
      >
        {description}
      </DescWrapper>
      <ContentWrapper>
        {count.map(
          ({ numberPrefix, curNumber, numberSuffix, description }, index) => (
            <Fade key={description} delay={index * 200}>
              <CardWrapper key={index}>
                <NumberWrapper>
                  {numberPrefix ?? ""} {curNumber} {numberSuffix ?? ""}
                </NumberWrapper>
                <DescWrapper className="body">{description}</DescWrapper>
                {index < count.length - 1 && <div className="line" />}
              </CardWrapper>
            </Fade>
          )
        )}
      </ContentWrapper>
      {buttonText && (
        <Fade bottom>
          <Button content={buttonText} href={buttonHref} target="_blank" />
        </Fade>
      )}
    </Wrapper>
  )
}
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  .line {
    width: 1px;
    height: 64px;
    background: rgba(255, 255, 255, 0.4);
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate(0, -50%);
    @media (${theme.lg}) {
      display: none;
    }
  }
`
const TitleWrapper = styled.div`
  color: ${theme.color.primary};
  text-align: center;
`
const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  @media (${theme.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
`
const CardWrapper = styled.div`
  position: relative;
  justify-items: center;
  width: 100%;
  display: grid;
  align-content: start;
  grid-template-columns: 1fr;
  padding: 38px;
  @media (${theme.lg}) {
    padding: 12px;
  }
`
const NumberWrapper = styled.h3`
  color: #fff;
  text-transform: none;
`
const DescWrapper = styled.div`
  max-width: 75vw;
  color: ${theme.color.textSecondary};
  text-align: center;
`
